import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { WifiOff, Check, RefreshCw } from 'lucide-react';

export default function OfflineBanner() {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [showBack, setShowBack] = useState(false);
  const [checking, setChecking] = useState(false);

  const checkConnection = useCallback(async () => {
    if (checking) return;
    setChecking(true);
    try {
      // Real connectivity check with cache-busting ping
      await fetch('/favicon.ico?' + Date.now(), { method: 'HEAD', cache: 'no-store' });
      setIsOffline(false);
      setShowBack(true);
      setTimeout(() => setShowBack(false), 2800);
    } catch {
      setIsOffline(true);
    } finally {
      setChecking(false);
    }
  }, [checking]);

  useEffect(() => {
    const handleOffline = () => {
      setIsOffline(true);
      setShowBack(false);
    };

    const handleOnline = () => {
      setShowBack(true);
      setIsOffline(false);
      setTimeout(() => setShowBack(false), 2800);
    };

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  if (typeof document === 'undefined') return null;

  const isAdmin = typeof window !== 'undefined' && window.location.pathname.startsWith('/admin');

  // Palette aligned 1:1 with Toast design
  const offlineTheme = {
    bg: 'bg-[#dc2626]', // Rich Red
    border: 'border-[#b91c1c]',
    shadow: 'shadow-xl shadow-red-950/25',
    icon: WifiOff,
    text: 'No Internet Connection'
  };

  const onlineTheme = {
    bg: 'bg-[#15803d]', // Rich Green
    border: 'border-[#166534]',
    shadow: 'shadow-xl shadow-emerald-950/25',
    icon: Check,
    text: 'Back Online'
  };

  const activeTheme = isOffline ? offlineTheme : onlineTheme;
  const IconComponent = activeTheme.icon;

  const isVisible = isOffline || showBack;

  return createPortal(
    <AnimatePresence>
      {isVisible && (
        <>
          {/* ── 1. MOBILE: Top Floating Dynamic Island Capsule (sm:hidden) ── */}
          <div className="sm:hidden fixed top-3 left-0 right-0 z-[99999] pointer-events-none flex justify-center px-4">
            <motion.div
              key="mobile-offline-capsule"
              initial={{ opacity: 0, y: -24, scale: 0.88 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -16, scale: 0.88 }}
              transition={{ type: 'spring', damping: 20, stiffness: 360 }}
              role="status"
              aria-live="polite"
              className={`pointer-events-auto h-9 max-w-[88vw] rounded-full flex items-center gap-2 pl-2 pr-2.5 text-white border ${activeTheme.bg} ${activeTheme.border} ${activeTheme.shadow} backdrop-blur-md`}
            >
              {/* Circular mini badge */}
              <div className="w-5 h-5 rounded-full bg-white/25 flex items-center justify-center shrink-0">
                <IconComponent size={11} strokeWidth={3} />
              </div>

              {/* Message */}
              <span className="text-xs font-semibold text-white truncate max-w-[200px] select-none leading-none">
                {activeTheme.text}
              </span>

              {/* Retry button on mobile when offline */}
              {isOffline && (
                <button
                  type="button"
                  onClick={checkConnection}
                  disabled={checking}
                  title="Retry connection"
                  aria-label="Retry connection"
                  className="w-5 h-5 rounded-full flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20 active:scale-90 transition-all shrink-0 cursor-pointer ml-0.5"
                >
                  <RefreshCw size={11} strokeWidth={2.5} className={checking ? 'animate-spin' : ''} />
                </button>
              )}
            </motion.div>
          </div>

          {/* ── 2. DESKTOP: Solid Boxed Bottom-Left Card (hidden sm:block) ── */}
          <motion.div
            key="desktop-offline-card"
            initial={{ opacity: 0, y: 18, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.96 }}
            transition={{ type: 'spring', damping: 25, stiffness: 350 }}
            role="status"
            aria-live="polite"
            className={`hidden sm:block fixed z-[99999] pointer-events-auto overflow-hidden rounded-lg border text-white ${activeTheme.bg} ${activeTheme.border} ${activeTheme.shadow} sm:bottom-6 ${isAdmin ? 'sm:left-[76px]' : 'sm:left-6'} sm:min-w-[320px] sm:max-w-md`}
          >
            <div className="flex items-center justify-between gap-3 px-4 py-3 min-w-0">
              {/* Left: Icon Badge & Bold White Text */}
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="w-6 h-6 rounded-md bg-white/20 flex items-center justify-center shrink-0">
                  <IconComponent size={14} strokeWidth={3} />
                </div>

                <div className="min-w-0">
                  <p className="text-[13px] font-bold text-white tracking-tight leading-snug truncate">
                    {activeTheme.text}
                  </p>
                  {isOffline && (
                    <p className="text-[11px] text-white/80 font-normal leading-tight">
                      Check your internet connection
                    </p>
                  )}
                </div>
              </div>

              {/* Right: Retry button when offline */}
              {isOffline && (
                <button
                  type="button"
                  onClick={checkConnection}
                  disabled={checking}
                  className="px-2.5 py-1 rounded bg-white/15 hover:bg-white/25 active:scale-95 text-white text-xs font-bold transition-all shrink-0 cursor-pointer flex items-center gap-1.5 disabled:opacity-60"
                >
                  <RefreshCw size={12} className={checking ? 'animate-spin' : ''} />
                  <span>{checking ? 'Checking' : 'Retry'}</span>
                </button>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
}
